import { FileType, FileTypeHeader } from "config/constants"
import CustomError from "models/CustomError"

function bytesToMegabytes(bytes: number) {
    if (bytes < 0) {
        return 0
    }
    return bytes / 1000000
}

function getFileTypeByHeader(fileTypeHeader: string): FileType | undefined {
    for (let type in FileTypeHeader) {
        if (FileTypeHeader[type].includes(fileTypeHeader)) {
            return type as FileType
        }
    }
}

function isFileTypeHeaderSupported(fileTypeHeader: string, supportedFileTypes: string[]) {
    const type = getFileTypeByHeader(fileTypeHeader)
    return !!type && supportedFileTypes.includes(type)
}

function isFileExtensionSupported(fileName: string, supportedExtensions: string[]) {
    const fileNameLowerCased = fileName.toLowerCase()
    return supportedExtensions.some(ext => fileNameLowerCased.endsWith(ext.toLowerCase()))
}

function isFileSizeValid(size: number, maxSizeMb: number) {
    return bytesToMegabytes(size) > maxSizeMb
}

function arrayBufferToBase64(arrayBuffer: ArrayBuffer, type: string): string {
    const base64String = btoa(new Uint8Array(arrayBuffer).reduce(function (data, byte) {
        return data + String.fromCharCode(byte)
    }, ""))
    return `data:${type};base64,${base64String}`
}

// returns base64 image preview if the file passes validation
function validateImageFile({
    file,
    extensions,
    types,
    maxSizeMb,
}: {
    file: File
    extensions: string[]
    types: FileType[]
    maxSizeMb: number
}): Promise<string> {
    if (!isFileExtensionSupported(file.name, extensions)) {
        throw new CustomError({ messageCode: "validation.fileTypeNotSupported" })
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onloadend = e => {
            if (!e.target || !(e.target.result instanceof ArrayBuffer)) {
                reject(new CustomError({ messageCode: "validation.failedToReadFile" }))
                return
            }

            const result: ArrayBuffer = e.target.result
            const arr = (new Uint8Array(result)).subarray(0, 4)
            let header = ""

            for (let item of arr) {
                header += item.toString(16)
            }

            if (!isFileTypeHeaderSupported(header, types)) {
                reject(new CustomError({ messageCode: "validation.fileTypeNotSupported" }))
                return
            }

            if (isFileSizeValid(file.size, maxSizeMb)) {
                reject(new CustomError({ messageCode: "validation.pictureSizeTooHigh" }))
                return
            }

            resolve(arrayBufferToBase64(result, getFileTypeByHeader(header) || ""))
        }

        reader.onerror = e => {
            console.log(e)
            reject(new CustomError({ messageCode: "validation.failedToReadFile" }))
        }

        reader.readAsArrayBuffer(file)
    })
}


function validateDocumentFile({
    file,
    extensions,
    types,
    maxSizeMb,
}: {
    file: File
    extensions: string[]
    types: FileType[]
    maxSizeMb: number
}): Promise<void> {
    if (!isFileExtensionSupported(file.name, extensions)) {
        throw new CustomError({ messageCode: "validation.fileTypeNotSupported" })
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onloadend = e => {
            if (!e.target || !(e.target.result instanceof ArrayBuffer)) {
                reject(new CustomError({ messageCode: "validation.failedToReadFile" }))
                return
            }

            const result: ArrayBuffer = e.target.result
            const arr = (new Uint8Array(result)).subarray(0, 4)
            let header = ""

            for (let item of arr) {
                header += item.toString(16)
            }

            if (file.type !== "text/plain" && !isFileTypeHeaderSupported(header, types)) {
                reject(new CustomError({ messageCode: "validation.fileTypeNotSupported" }))
                return
            }

            if (isFileSizeValid(file.size, maxSizeMb)) {
                reject(new CustomError({ messageCode: "validation.fileSizeTooHigh" }))
                return
            }

            resolve()
        }

        reader.onerror = e => {
            console.log(e)
            reject(new CustomError({ messageCode: "validation.failedToReadFile" }))
        }

        reader.readAsArrayBuffer(file)
    })
}


export const fileUtils = import.meta.env.NODE_ENV === "test"
    ? {
        bytesToMegabytes,
        validateImageFile,
        validateDocumentFile,
        isFileExtensionSupported,
    }
    : null

export default {
    validateImageFile,
    validateDocumentFile,
}
